import { ReactNode, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";

import { MediaFullScreen } from "../../../../common/components/media/MediaFullScreen";
import { formatTimestamp } from "../../../../common/utils/timestampUtils";
import { RecordingViewModel } from "../../models/recordingViewModel";

export type ExpandableData = { label: string; value: ReactNode };

export type EventRecords = {
  url: string;
  timestamp?: string;
  rateMCF?: string;
};

interface ExpandableEventDetailsProps {
  colSpan: number;
  records: RecordingViewModel[];
  expandableData: ExpandableData[];
  latestRecordingTimestamp?: string;
  fetchNextPage: () => Promise<void>;
  shouldLoadMore?: boolean;
}

const ExpandableEventDetails = ({
  colSpan,
  records,
  expandableData,
  latestRecordingTimestamp,
  shouldLoadMore,
  fetchNextPage,
}: ExpandableEventDetailsProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const handleExpand = () => {
    setExpanded(v => !v);
  };

  return (
    <>
      <TableRow>
        <TableCell
          colSpan={colSpan}
          sx={{
            border: 0,
            p: 0,
            background: theme.palette.otherBackgroundLight.main,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              py: "8px",
              pl: "16px",
            }}
          >
            <IconButton size="medium" sx={{ p: 0 }} onClick={handleExpand}>
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                  color: theme.palette.otherTextTertiary.main,
                }}
              />
            </IconButton>
            <Typography
              variant="body2Regular"
              sx={{
                ml: "8px",
                color: theme.palette.text.secondary,
              }}
            >
              {records.length > 0
                ? `${records.length} Recording${records.length > 1 ? "s" : ""}`
                : "No Recordings"}
            </Typography>
            <Typography
              variant="body2Regular"
              sx={{
                ml: "98px",
                color: theme.palette.text.secondary,
              }}
            >
              The last: {latestRecordingTimestamp}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={colSpan}
          sx={{
            p: 0,
            border: 0,
            background: theme.palette.otherBackgroundLight.main,
          }}
        >
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                px: "16px",
                py: "12px",
                mx: "16px",
                mb: "16px",
                borderRadius: "0.625rem",
                background: theme.palette.otherBackground.main,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                {expandableData.map(item => (
                  <Box
                    key={item.label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <Typography
                      variant="body2Regular"
                      fontWeight="fontWeightMedium"
                      color="text.secondary"
                      sx={{ minWidth: 188 }}
                    >
                      {item.label}
                    </Typography>
                    <Typography variant="body2Regular">{item.value}</Typography>
                  </Box>
                ))}
              </Box>
              {records.length > 0 && (
                <>
                  <Divider sx={{ my: "16px" }} />

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "16px",
                    }}
                  >
                    {records.map(
                      ({
                        id,
                        rawEventInfo,
                        timestamp,
                        keyFrame,
                        mediaOutput,
                      }) => (
                        <Box
                          key={id}
                          sx={{
                            display: "flex",
                            gap: "8px",
                            position: "relative",
                          }}
                        >
                          <Box>
                            <MediaFullScreen
                              keyFrame={keyFrame}
                              mediaOutput={mediaOutput}
                              height={67}
                            />
                          </Box>
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <Typography
                                variant="body2Regular"
                                fontWeight="fontWeightMedium"
                                color="text.secondary"
                                sx={{ minWidth: 100 }}
                              >
                                Timestamp
                              </Typography>
                              <Typography variant="body2Regular">
                                {formatTimestamp(timestamp)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                mt: "8px",
                              }}
                            >
                              <Typography
                                variant="body2Regular"
                                fontWeight="fontWeightMedium"
                                color="text.secondary"
                                sx={{ minWidth: 100 }}
                              >
                                Rate MCF
                              </Typography>
                              <Typography variant="body2Regular">
                                {rawEventInfo?.data?.rate_mcf}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )
                    )}
                  </Box>

                  {shouldLoadMore && (
                    <>
                      <Divider sx={{ my: "16px" }} />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={fetchNextPage}
                      >
                        <Typography variant="buttonMedium">
                          Load More
                        </Typography>
                      </Button>
                    </>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default ExpandableEventDetails;
